import {
  AlertColor,
  CircularProgress,
  Link,
  Typography,
  useTheme,
} from '@mui/material';
import { FC, MouseEvent, RefObject, useRef, useState } from 'react';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useCustomNavigate } from 'utils/hooks/useCustomNavigate';
import strings from '../../../common/Translation/Translate';
import PrimaryButton from '../../../components/Button/PrimaryButton';
import RecaptchaNotice from '../../../components/Captcha/RecaptchaNotice';
import Form, { FormDataModel } from '../../../components/Form';
import axiosInstance from '../../../utils/axios';
import { identifiers } from '../../../utils/constants/identifiers';
import { URLS } from '../../../utils/constants/urls';
import { removeErrorFieldsFromValues } from '../../../utils/validators';
import { RECAPTCHA_ACTIONS } from '../../../utils/recaptchaActions';
import AuthPageShell, { AuthPageMessage, authLinkSx, authPrimaryButtonSx } from '../AuthPageShell';
import { ForgotForm } from './ForgotForm';

const ForgotPasswordContent: FC = () => {
  const Navigate = useCustomNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const theme = useTheme();
  const forgotForm: RefObject<Form | null | undefined> = useRef();
  const [hasError, setHasError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<AuthPageMessage>({
    display: false,
    severity: null,
    message: '',
  });

  const setActionMessage = (
    display = false,
    severity: AlertColor | null = 'warning',
    msg = '',
  ) => {
    setMessage({ display, severity, message: msg });
  };

  const handleForgot = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const { getFormData } = forgotForm.current as {
      getFormData: () => { formData: FormDataModel; isFormValid: boolean };
    };
    const { formData, isFormValid } = getFormData();
    const body = removeErrorFieldsFromValues(formData);
    try {
      setHasError(false);
      if (isFormValid) {
        if (!executeRecaptcha) {
          setActionMessage(true, 'warning', 'reCAPTCHA is not ready');
          return;
        }
        setLoading(true);
        const captchaRes = await executeRecaptcha(RECAPTCHA_ACTIONS.forgotPassword);
        const { status } = await axiosInstance.post(URLS.forgot, {
          identifier: body.email,
          mailType: 'FORGOT_PASSWORD',
          captchaResponse: {
            captchaRes,
            captchaAction: RECAPTCHA_ACTIONS.forgotPassword,
          },
        });
        if (status === 200) {
          Navigate(`/reset-password?email=${body.email}`);
        }
      } else {
        setHasError(true);
      }
    } catch (error: any) {
      setActionMessage(true, 'warning', error.response ? error.response.data.message : error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPageShell
      title={strings.forgotPassword || 'Forgot Password'}
      subtitle="Enter your email and we'll send you a verification code to reset your password."
      message={message}
      onCloseNotification={value => setMessage(prev => ({ ...prev, display: value }))}>
      <form onSubmit={handleForgot as any}>
        <Form hasError={hasError} ref={forgotForm as RefObject<Form>} model={ForgotForm(strings)} values={{}} />
        <PrimaryButton
          data-testid="forgotPassword-btn"
          disabled={loading}
          sx={authPrimaryButtonSx}
          onClick={handleForgot}
          type="submit"
          fullWidth>
          {loading && <CircularProgress sx={{ mr: 1 }} size={20} />}
          {strings.forgotPassword}
        </PrimaryButton>
        <RecaptchaNotice />
      </form>
      <Link underline="none">
        <Typography variant="body2" sx={authLinkSx(theme.palette.primary.main)} onClick={() => Navigate('/login')}>
          {strings.haveAnAcountText}
        </Typography>
      </Link>
    </AuthPageShell>
  );
};

const ForgotPassword: FC = () => (
  <GoogleReCaptchaProvider
    reCaptchaKey={identifiers.REACT_APP_GOOGLE_RECAPTCHA}
    scriptProps={{
      async: true,
      defer: true,
      appendTo: 'head',
    }}>
    <ForgotPasswordContent />
  </GoogleReCaptchaProvider>
);

export default ForgotPassword;
